'use client';

import Image from 'next/image';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useLocale, useTranslations } from 'next-intl';

import { Link as IntlLink, usePathname, useRouter } from '@/i18n/navigation';
import { DOCS_URL, LOCALES, type LocaleCode } from '@/lib/constants';
import { getTheme, subscribe, toggleTheme } from '@/lib/theme';

import { useAuthModal } from './AuthModalProvider';
import { Clock } from './Clock';

type ServiceStatus = 'op' | 'deg' | 'down';

// System-status strip. Static for now — `// Seam:` a real status API would feed
// this (e.g. fetch from status.onekyc.io). All services operational.
const SERVICES: ReadonlyArray<{ code: string; status: ServiceStatus }> = [
	{ code: 'KYC', status: 'op' },
	{ code: 'KYB', status: 'op' },
	{ code: 'AML', status: 'op' },
	{ code: 'API', status: 'op' },
	{ code: 'MONITORING', status: 'op' },
	{ code: 'WEBHOOKS', status: 'op' },
];

type NavTarget =
	| { kind: 'section'; id: string }
	| { kind: 'route'; href: '/help' | '/pricing' }
	| { kind: 'external'; href: string };

interface NavItem {
	num: string;
	key: 'init' | 'products' | 'modules' | 'pricing' | 'apiDocs' | 'support';
	target: NavTarget;
}

const NAV_ITEMS: readonly NavItem[] = [
	{ num: '01', key: 'init', target: { kind: 'section', id: 'landing' } },
	{ num: '02', key: 'products', target: { kind: 'section', id: 'products' } },
	{ num: '03', key: 'modules', target: { kind: 'section', id: 'modules' } },
	{ num: '04', key: 'pricing', target: { kind: 'route', href: '/pricing' } },
	{ num: '05', key: 'apiDocs', target: { kind: 'external', href: DOCS_URL } },
	{ num: '06', key: 'support', target: { kind: 'route', href: '/help' } },
];

const SECTION_IDS = NAV_ITEMS.filter((i) => i.target.kind === 'section').map(
	(i) => (i.target as { id: string }).id,
);

// Row horizontal padding — matches the prototype's clamp(16px,4vw,40px).
const ROW_X = 'px-[clamp(16px,4vw,40px)]';

function dotStyle(status: ServiceStatus): React.CSSProperties {
	const base: React.CSSProperties = {
		width: '9px',
		height: '9px',
		borderRadius: '50%',
		border: '1.5px solid currentColor',
		flex: '0 0 auto',
	};
	if (status === 'op') return { ...base, background: 'currentColor' };
	if (status === 'deg')
		return {
			...base,
			background: 'linear-gradient(90deg,currentColor 50%,transparent 50%)',
		};
	return { ...base, background: 'transparent' };
}

export function SiteHeader() {
	const t = useTranslations('Nav');
	const tCta = useTranslations('Cta');
	const tStatus = useTranslations('Status');
	const locale = useLocale();
	const router = useRouter();
	const pathname = usePathname();
	const { open: openAuth } = useAuthModal();

	const theme = useSyncExternalStore(subscribe, getTheme, () => 'light');
	const isDark = theme === 'dark';

	const [langOpen, setLangOpen] = useState(false);
	const [mobileOpen, setMobileOpen] = useState(false);
	const [activeSection, setActiveSection] = useState<string | null>(null);
	const langRef = useRef<HTMLDivElement>(null);

	// Scrollspy — highlight the nav item whose section is in view. Sections only
	// exist on the home page; elsewhere the observer simply never fires.
	useEffect(() => {
		const sections = SECTION_IDS.map((id) =>
			document.getElementById(id),
		).filter((el): el is HTMLElement => el !== null);
		if (sections.length === 0) return;
		const observer = new IntersectionObserver(
			(entries) => {
				const visible = entries
					.filter((e) => e.isIntersecting)
					.sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
				if (visible) setActiveSection(visible.target.id);
			},
			{ rootMargin: '-45% 0px -45% 0px', threshold: [0, 0.25, 0.5, 1] },
		);
		sections.forEach((el) => observer.observe(el));
		return () => observer.disconnect();
	}, [pathname]);

	// Close the language menu on outside click / Escape.
	useEffect(() => {
		if (!langOpen) return;
		const onPointer = (e: MouseEvent) => {
			if (!langRef.current?.contains(e.target as Node)) setLangOpen(false);
		};
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') setLangOpen(false);
		};
		document.addEventListener('mousedown', onPointer);
		document.addEventListener('keydown', onKey);
		return () => {
			document.removeEventListener('mousedown', onPointer);
			document.removeEventListener('keydown', onKey);
		};
	}, [langOpen]);

	const overall: ServiceStatus = SERVICES.some((s) => s.status === 'down')
		? 'down'
		: SERVICES.some((s) => s.status === 'deg')
			? 'deg'
			: 'op';
	const overallText =
		overall === 'op'
			? tStatus('operational')
			: overall === 'deg'
				? tStatus('degraded')
				: tStatus('outage');

	const current = LOCALES.find((l) => l.code === locale) ?? LOCALES[0];
	// Arabic drops latin letter-spacing and uses a sans face, per the prototype.
	const ar = locale === 'ar';

	const selectLang = (code: LocaleCode) => {
		setLangOpen(false);
		setMobileOpen(false);
		router.replace(pathname, { locale: code });
	};

	const scrollToSection = (id: string) => {
		setMobileOpen(false);
		document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
	};

	// Shared utility-button base (login / language / theme in row 2).
	const utilBtn =
		'flex min-h-6 cursor-crosshair items-center whitespace-nowrap bg-transparent py-1 font-mono text-[11px] text-fg transition-opacity hover:opacity-60 focus-visible:opacity-100 focus-visible:ring-1 focus-visible:ring-fg focus-visible:outline-none';

	const renderNavItem = (item: NavItem, mobile: boolean) => {
		const label = t(item.key);
		const isActive =
			item.target.kind === 'section' && activeSection === item.target.id;
		const labelLs = ar
			? 'tracking-normal'
			: mobile
				? 'tracking-[1px]'
				: 'tracking-[1.5px]';
		const cls = mobile
			? `border-border flex w-full cursor-crosshair items-baseline gap-[14px] border-b px-0.5 py-[14px] text-start ${
					isActive ? 'text-fg' : 'text-muted hover:text-fg'
				} focus-visible:ring-1 focus-visible:ring-fg focus-visible:outline-none`
			: `flex cursor-crosshair flex-col items-center gap-1 border-b-2 ${
					isActive
						? 'border-fg text-fg'
						: 'border-transparent text-muted hover:text-fg'
				} px-0.5 pt-1 pb-1.5 transition-opacity hover:opacity-100 focus-visible:opacity-100 focus-visible:ring-1 focus-visible:ring-fg focus-visible:outline-none`;
		const inner = (
			<>
				<span className="font-mono text-[11px] tracking-[1px]">{item.num}</span>
				<span
					className={`font-mono uppercase ${mobile ? 'text-base' : 'text-[13px]'} ${labelLs}`}
				>
					{label}
				</span>
			</>
		);

		if (item.target.kind === 'external') {
			return (
				<a
					key={item.num}
					href={item.target.href}
					target="_blank"
					rel="noopener noreferrer"
					title={`${label} — ${t('opensInNewTab')}`}
					className={`${cls} cursor-crosshair no-underline`}
					onClick={() => setMobileOpen(false)}
				>
					{inner}
				</a>
			);
		}
		if (item.target.kind === 'route') {
			return (
				<IntlLink
					key={item.num}
					href={item.target.href}
					className={`${cls} cursor-crosshair no-underline`}
					onClick={() => setMobileOpen(false)}
				>
					{inner}
				</IntlLink>
			);
		}
		const { id } = item.target;
		// Sections only exist on the home page. Off-home (e.g. /pricing, /help)
		// the scroll target isn't present, so link to home + hash; the browser
		// navigates home and scrolls to the section. On home, smooth-scroll.
		if (pathname !== '/') {
			return (
				<IntlLink
					key={item.num}
					href={{ pathname: '/', hash: id }}
					className={`${cls} cursor-crosshair no-underline`}
					onClick={() => setMobileOpen(false)}
				>
					{inner}
				</IntlLink>
			);
		}
		return (
			<button
				key={item.num}
				type="button"
				onClick={() => scrollToSection(id)}
				aria-current={isActive ? 'true' : undefined}
				className={cls}
			>
				{inner}
			</button>
		);
	};

	return (
		<header className="bg-bg text-fg fixed inset-x-0 top-0 z-[100] pt-[env(safe-area-inset-top)]">
			{/* Row 1 — system status */}
			<div
				role="status"
				aria-label={tStatus('regionLabel')}
				className={`border-border flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b py-[9px] font-mono ${ROW_X}`}
			>
				<div className="flex items-center gap-[9px]">
					<span
						className="auth-cursor"
						style={dotStyle(overall)}
						aria-hidden="true"
					/>
					<span
						className={`text-[11px] uppercase ${ar ? 'tracking-normal' : 'tracking-[1.5px]'}`}
					>
						{overallText}
					</span>
				</div>
				<div className="hidden flex-wrap items-center gap-x-[18px] gap-y-2 min-[880px]:flex">
					{SERVICES.map((s) => (
						<span key={s.code} className="flex items-center gap-[7px]">
							<span style={dotStyle(s.status)} aria-hidden="true" />
							<span
								className={`text-muted text-[11px] uppercase ${ar ? 'tracking-normal' : 'tracking-[1px]'}`}
							>
								{s.code}
							</span>
						</span>
					))}
				</div>
			</div>

			{/* Row 2 — utilities */}
			<div
				className={`border-border flex flex-wrap items-center justify-between gap-x-[18px] gap-y-2.5 border-b py-[10px] ${ROW_X}`}
			>
				<Clock className="text-muted !text-[11px] tracking-[1.5px]" />
				<div className="flex flex-wrap items-center gap-x-[18px] gap-y-2">
					<button
						type="button"
						onClick={() => openAuth('login')}
						className={`${utilBtn} gap-[7px] no-underline max-[880px]:hidden ${ar ? 'tracking-normal' : 'tracking-[1.5px]'}`}
					>
						<span
							className="inline-block h-1.5 w-1.5 rotate-45 border border-current"
							aria-hidden="true"
						/>
						{t('login')}
					</button>

					<div ref={langRef} className="relative z-[60] max-[880px]:hidden">
						<button
							type="button"
							aria-haspopup="menu"
							aria-expanded={langOpen}
							onClick={() => setLangOpen((o) => !o)}
							className={`${utilBtn} gap-1.5 tracking-[1px]`}
						>
							<span
								className="inline-block h-[11px] w-[11px] rounded-full border border-current"
								aria-hidden="true"
							/>
							{current.name}
							<span className="opacity-55" aria-hidden="true">
								▾
							</span>
						</button>
						{langOpen && (
							<div
								role="menu"
								aria-label={current.name}
								className="border-border bg-bg absolute end-0 top-[calc(100%+10px)] z-[60] min-w-[168px] border py-1 shadow-[0_10px_30px_rgba(0,0,0,0.12)] dark:shadow-[0_10px_30px_rgba(0,0,0,0.55)]"
							>
								{LOCALES.map((l) => (
									<button
										key={l.code}
										type="button"
										role="menuitemradio"
										aria-checked={l.code === locale}
										onClick={() => selectLang(l.code)}
										className={`block w-full cursor-crosshair px-4 py-[9px] text-start text-[12.5px] whitespace-nowrap transition-opacity hover:opacity-100 ${
											l.code === 'ar'
												? 'font-sans tracking-normal'
												: 'font-mono tracking-[0.5px]'
										} ${
											l.code === locale
												? 'text-fg font-bold opacity-100'
												: 'text-fg opacity-55'
										}`}
									>
										{l.name}
									</button>
								))}
							</div>
						)}
					</div>

					<button
						type="button"
						onClick={toggleTheme}
						aria-pressed={isDark}
						className={`${utilBtn} gap-1.5 tracking-[1px]`}
					>
						<span
							className="inline-block h-3 w-3 rounded-full border border-current"
							style={{ background: isDark ? 'currentColor' : 'transparent' }}
							aria-hidden="true"
						/>
						{isDark ? 'LIGHT' : 'DARK'}
					</button>
				</div>
			</div>

			{/* Row 3 — brand · nav · CTA */}
			<nav
				aria-label={t('mainNavLabel')}
				className={`flex flex-wrap items-center justify-between gap-x-6 gap-y-4 py-4 ${ROW_X}`}
			>
				<IntlLink
					href="/"
					className="flex min-h-6 cursor-crosshair items-center gap-[13px] no-underline"
					onClick={() => setMobileOpen(false)}
				>
					<span className="text-muted font-mono text-[9px] leading-none tracking-[2px]">
						SYS.ID
					</span>
					<Image
						src="/logo.svg"
						alt="ONE-KYC"
						width={103}
						height={24}
						data-logo
						className="h-5 w-auto sm:h-6"
						priority
					/>
				</IntlLink>

				<div className="hidden flex-wrap items-end gap-x-6 gap-y-3 min-[880px]:flex">
					{NAV_ITEMS.map((item) => renderNavItem(item, false))}
				</div>

				<div className="flex items-center gap-3">
					<button
						type="button"
						onClick={() => openAuth('register')}
						className={`bg-fg text-bg focus-visible:ring-fg hidden min-h-11 cursor-crosshair items-center gap-2 px-[22px] py-[14px] font-mono text-[12px] uppercase no-underline transition-opacity hover:opacity-85 focus-visible:ring-1 focus-visible:outline-none min-[880px]:inline-flex ${ar ? 'tracking-normal' : 'tracking-[2px]'}`}
					>
						{tCta('getStarted')}
						<span aria-hidden="true">-&gt;</span>
					</button>
					<button
						type="button"
						onClick={() => setMobileOpen((o) => !o)}
						aria-label={`${mobileOpen ? 'CLOSE' : 'MENU'} — ${
							mobileOpen ? t('closeMenu') : t('openMenu')
						}`}
						aria-expanded={mobileOpen}
						aria-controls="site-mobile-menu"
						className="focus-visible:ring-fg flex cursor-crosshair flex-col items-center gap-1 bg-transparent p-1 font-mono text-[9px] focus-visible:ring-1 focus-visible:outline-none min-[880px]:hidden"
					>
						<span className="bg-fg block h-0.5 w-[22px]" />
						<span className="bg-fg block h-0.5 w-[22px]" />
						<span className="bg-fg block h-0.5 w-[22px]" />
						<span className="tracking-[1.5px]">
							{mobileOpen ? 'CLOSE' : 'MENU'}
						</span>
					</button>
				</div>
			</nav>

			{/* Mobile menu — nav list + language chips + auth actions */}
			{mobileOpen && (
				<div
					id="site-mobile-menu"
					className={`border-border bg-bg max-h-[calc(100dvh-9.5rem)] overflow-y-auto overscroll-contain border-t pt-1.5 pb-[calc(1.5rem+env(safe-area-inset-bottom))] min-[880px]:hidden ${ROW_X}`}
				>
					<div className="flex flex-col">
						{NAV_ITEMS.map((item) => renderNavItem(item, true))}
					</div>

					<div className="text-muted mt-5 mb-2.5 font-mono text-[10px] tracking-[2px]">
						LANGUAGE
					</div>
					<div className="flex flex-wrap gap-x-2.5 gap-y-2">
						{LOCALES.map((l) => (
							<button
								key={l.code}
								type="button"
								aria-pressed={l.code === locale}
								onClick={() => selectLang(l.code)}
								className={`border-border inline-flex min-h-11 cursor-crosshair items-center border px-3 py-2 text-[12px] transition-opacity ${
									l.code === 'ar'
										? 'font-sans tracking-normal'
										: 'font-mono tracking-[0.5px]'
								} ${
									l.code === locale
										? 'font-bold opacity-100'
										: 'opacity-60 hover:opacity-100'
								}`}
							>
								{l.name}
							</button>
						))}
					</div>

					<div className="mt-[22px] flex gap-3">
						<button
							type="button"
							onClick={() => {
								setMobileOpen(false);
								openAuth('login');
							}}
							className={`border-border text-fg flex flex-1 cursor-crosshair items-center justify-center border px-[22px] py-[14px] font-mono text-[12px] uppercase no-underline hover:border-current ${ar ? 'tracking-normal' : 'tracking-[2px]'}`}
						>
							{t('login')}
						</button>
						<button
							type="button"
							onClick={() => {
								setMobileOpen(false);
								openAuth('register');
							}}
							className={`bg-fg text-bg flex flex-1 cursor-crosshair items-center justify-center gap-2 px-[22px] py-[14px] font-mono text-[12px] uppercase no-underline transition-opacity hover:opacity-85 ${ar ? 'tracking-normal' : 'tracking-[2px]'}`}
						>
							{tCta('getStarted')}
							<span aria-hidden="true">-&gt;</span>
						</button>
					</div>
				</div>
			)}
		</header>
	);
}
